import { useCallback, useContext, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { getCapChartSecurities } from '@dealstack/frontend/app/services/CapTableData.service.ts';
import { CapHeaderContext } from '@dealstack/frontend/app/pages/Project/Cap/common/CapPageHeader/useCapPageHeaderController.tsx';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import {
	flattenChartSecurities,
	resolveSecuritySelection,
} from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/capChartSecurities.utils.ts';

const SELECTION_DEBOUNCE_MS = 500;

export const useCapChartSecuritiesController = () => {
	const { dealId } = useContext(DealContext);
	const { filterDate } = useContext(CapHeaderContext);
	// Exclusions rather than selections, so a re-fetch for another date keeps the user's choice.
	const [excludedSecurityIds, setExcludedSecurityIds] = useState<ReadonlySet<string>>(new Set());

	const isoFilterDate = useMemo(() => filterDate?.toISOString(), [filterDate]);

	const { data: chartSecurities, isLoading: isLoadingSecurities } = useQuery({
		queryKey: ['chartSecurities', dealId, isoFilterDate],
		queryFn: () => {
			if (!dealId) {
				return Promise.resolve([]);
			}
			return getCapChartSecurities({
				filterDate: isoFilterDate,
			});
		},
		enabled: !!dealId,
		staleTime: 5 * 60 * 1000,
		placeholderData: keepPreviousData,
	});

	const securityIds = useMemo(
		() => flattenChartSecurities(chartSecurities).map(({ id }) => id),
		[chartSecurities]
	);

	const selectedSecurities = useMemo(
		() => resolveSecuritySelection(securityIds, excludedSecurityIds),
		[securityIds, excludedSecurityIds]
	);

	const selectedSecuritiesCount = useMemo(
		() => [...selectedSecurities.values()].filter(Boolean).length,
		[selectedSecurities]
	);
	const totalSecuritiesCount = selectedSecurities.size;

	/** Undefined while every security is selected. */
	const selectedSecurityIds = useMemo(() => {
		if (selectedSecuritiesCount === totalSecuritiesCount) {
			return undefined;
		}
		return [...selectedSecurities]
			.filter(([, isSelected]) => isSelected)
			.map(([securityId]) => securityId);
	}, [selectedSecurities, selectedSecuritiesCount, totalSecuritiesCount]);
	// The chart refetches on this, so a run of ticks costs one request rather than one each.
	const [appliedSecurityIds] = useDebouncedValue(selectedSecurityIds, SELECTION_DEBOUNCE_MS);

	const setSecuritiesSelected = useCallback(
		(changedIds: string[], value: boolean) => {
			setExcludedSecurityIds((prev) => {
				const next = new Set(prev);
				for (const securityId of changedIds) {
					if (value) {
						next.delete(securityId);
					} else {
						next.add(securityId);
					}
				}
				// At least one listed security stays selected; clearing the last one is ignored.
				return securityIds.some((id) => !next.has(id)) ? next : prev;
			});
		},
		[securityIds]
	);

	const selectAllSecurities = useCallback(() => setExcludedSecurityIds(new Set()), []);

	return {
		dealId,
		isoFilterDate,
		chartSecurities,
		isLoadingSecurities,
		selectedSecurities,
		selectedSecuritiesCount,
		totalSecuritiesCount,
		appliedSecurityIds,
		isFilteringSecurities: selectedSecurityIds !== undefined,
		setSecuritiesSelected,
		selectAllSecurities,
	};
};
